import React from 'react';
import PropTypes from 'prop-types';

import Button from '$partials/ui/button';
import Image from '$partials/media/image';
import Logo from '$partials/icons/logo';
import { IconChevronSprite } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const StravaBanner = ({ id }) => {
    const items = Array.from(Array(40).keys());
    return (
        <div className='bg-black flex py-5'>
            <ul className={`ticker secondary-hero-ticker ticker-${id}`}>
                {items.map((_, i) => {
                    return (
                        <li
                            className={'ticker__item inline-block mr-48'}
                            key={i}>
                            <Logo className='h-[14px]' />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ModuleSecondaryHero = ({
    ctaLabel,
    ctaLink,
    eyebrowIcon,
    eyebrowTitle,
    image,
    imageMd,
    title,
    showTicker
}) => {
    const { formatURI } = useLocale();

    const desktopImage = imageMd || image;
    const mobileImage = image || imageMd;

    return (
        <div className='h-[281px] sm:h-[432px] md:h-[810px] relative z-0'>
            <div className='absolute top-0 left-0 right-0 bottom-0 hide-overflow'>
                <div className='max-w-screen sm:sticky top-0'>

                    {/* Desktop */}
                    {desktopImage && (
                        <figure className='hidden sm:block'>
                            <div
                                className='h-full w-full'>
                                {desktopImage?.contentType?.includes('video/') ?
                                    (
                                        <video
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            className='w-full h-full object-cover'
                                            src={desktopImage?.src}
                                            width={'100%'} />
                                    ) : (
                                        <Image
                                            {...desktopImage}
                                            className='w-full h-full object-cover'
                                            containerClassName='w-full h-full relative'
                                            height='100%'
                                            loadWidth={1920}
                                            width='100%' />
                                    )}

                            </div>
                        </figure>
                    )}

                    {/* Mobile */}
                    {mobileImage && (
                        <div className='h-[281px] w-full sm:hidden'>
                            {mobileImage?.includes('video/') ?
                                (
                                    <video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        className='w-full h-full object-cover'
                                        src={mobileImage?.src}
                                        width={'100%'} />
                                ) : (
                                    <Image
                                        {...mobileImage}
                                        className='w-full h-full object-cover'
                                        containerClassName='w-full h-full relative'
                                        height='100%'
                                        loadWidth={1162}
                                        width='100%' />
                                )}
                        </div>
                    )}

                    {showTicker && (
                        <div className='overflow-hidden'>

                            <StravaBanner id={'1'} />
                        </div>
                    )}

                </div>
                <div className='absolute bottom-0 left-0 w-full z-10 inset-0 flex sm:block'>
                    <div className='relative sm:sticky sm:bottom-0 sm:top-[50vh] mb-60 pt-60 justify-end sm:justify-center layout-container flex flex-col'>
                        {eyebrowTitle && (
                            <div className='flex items-center px-10 sm:px-5 md:px-10 mb-20 md:mb-30'>
                                {eyebrowIcon && (
                                    <div
                                        className='mr-5'
                                        style={{
                                            fontSize: '18px',
                                            width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em`,
                                            maxWidth: '100%'
                                        }}>
                                        <Image
                                            {...eyebrowIcon}
                                            loading={''} />
                                    </div>
                                )}
                                <p className='inline-block type-style-7 text-white'>
                                    {eyebrowTitle}
                                </p>
                            </div>
                        )}
                        <p className='px-10 sm:px-5 md:px-10 w-full sm:w-7/12 type-style-2 md:type-style-1 text-white'>
                            {title}
                        </p>
                        {ctaLabel && ctaLink && (
                            <div className='px-10 sm:px-5 md:px-10 flex self-start'>
                                <Button
                                    as='a'
                                    className='flex items-center rounded-button type-style-7 text-white button button--primary px-15 py-13 mt-20 md:mt-30'
                                    href={formatURI(ctaLink)}>
                                    <span>
                                        {ctaLabel}
                                    </span>
                                    <span className='button__icon button__icon--animate'>
                                        <IconChevronSprite />
                                    </span>
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    displayHeroImageWhole: PropTypes.bool,
    eyebrowIcon: PropTypes.object,
    eyebrowTitle: PropTypes.string,
    image: PropTypes.object,
    imageMd: PropTypes.object,
    title: PropTypes.string,
    parent: PropTypes.string,
    showTicker: PropTypes.bool,
};

StravaBanner.propTypes = {
    id: PropTypes.string,
};

ModuleSecondaryHero.propTypes = propTypes;

export default ModuleSecondaryHero;
